import { useEffect, useRef, useState, useCallback } from 'react';

function buildLeaderboardPayload(state, user) {
  const reviews = (state.publishedReviews || []).slice().sort((a, b) => b.score - a.score);
  const best = reviews[0];
  return {
    display_name: user?.displayName || user?.primaryEmail || 'Anonymous Grower',
    total_revenue: state.totalRevenue || 0,
    best_quality: best?.score ?? 0,
    best_strain: best?.strainName ?? best?.channel ?? null,
    game_day: state.gameDay || 1,
    stage: state.stage || 1,
    reputation: Math.round(state.reputation || 0),
    best_review_quote: best?.quote ?? null,
  };
}

async function apiSave(accessToken, state, keepalive = false) {
  const { ui: _ui, ...toSave } = state;
  const body = JSON.stringify(toSave);
  console.log('[cloud] Save payload size:', (body.length / 1024).toFixed(1), 'KB');
  return fetch('/api/saves', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body,
    // keepalive has a 64KB limit — only use it for unload handler
    keepalive,
  });
}

async function apiPushLeaderboard(accessToken, state, user) {
  return fetch('/api/leaderboard', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(buildLeaderboardPayload(state, user)),
    keepalive: true,
  });
}

async function apiLoad(accessToken) {
  const res = await fetch('/api/saves', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    console.warn('[cloud] Load returned', res.status, res.statusText);
    return null;
  }
  const data = await res.json();
  console.log('[cloud] Loaded save from cloud, keys:', data ? Object.keys(data).length : 'null');
  return data;
}

// Syncs game state to/from the cloud while the player is signed in.
// Returns { cloudStatus } — 'saving' | 'saved' | 'error' | null
export function useCloudSync(state, dispatch, user) {
  const prevUserRef = useRef(null);
  const stateRef = useRef(state);
  const tokenRef = useRef(null);
  const savedTimerRef = useRef(null);
  const skipUnloadRef = useRef(false);
  stateRef.current = state;

  const [cloudStatus, setCloudStatus] = useState(null); // null | 'saving' | 'saved' | 'error'

  const showSaved = useCallback(() => {
    setCloudStatus('saved');
    clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setCloudStatus(null), 3000);
  }, []);

  const doSave = useCallback(async (token, stateSnapshot) => {
    setCloudStatus('saving');
    try {
      const res = await apiSave(token, stateSnapshot, false); // no keepalive — avoids 64KB limit
      if (res.ok) {
        console.log('[cloud] Save OK (day', stateSnapshot.gameDay, ')');
        showSaved();
      } else {
        const text = await res.text().catch(() => '');
        console.warn('[cloud] Save returned', res.status, text);
        setCloudStatus('error');
      }
    } catch (err) {
      console.warn('[cloud] Save failed (network/size error):', err.message || err);
      setCloudStatus('error');
    }
  }, [showSaved]);

  // Refresh cached access token whenever user changes
  useEffect(() => {
    if (!user) {
      tokenRef.current = null;
      setCloudStatus(null);
      return;
    }
    user.getAccessToken().then(t => { tokenRef.current = t; }).catch(() => {});
  }, [user]);

  // On sign-in (user goes from null → object), load cloud save
  useEffect(() => {
    const wasGuest = !prevUserRef.current;
    const isNowSignedIn = !!user;
    prevUserRef.current = user;

    if (!wasGuest || !isNowSignedIn) return;

    console.log('[cloud] Sign-in detected, loading cloud save...');
    setCloudStatus('saving');

    (async () => {
      try {
        // Retry up to 3 times in case token isn't ready immediately after OAuth callback
        let token = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          if (attempt > 0) await new Promise(r => setTimeout(r, 1500));
          token = await user.getAccessToken();
          if (token) break;
          console.warn('[cloud] getAccessToken() returned null, retry', attempt + 1);
        }
        if (!token) {
          console.warn('[cloud] Could not get access token after retries');
          setCloudStatus('error');
          return;
        }
        tokenRef.current = token;
        const cloudSave = await apiLoad(token);
        if (cloudSave) {
          console.log('[cloud] Dispatching LOAD_GAME with cloud save (day', cloudSave.gameDay, ')');
          dispatch({ type: 'LOAD_GAME', payload: { savedState: cloudSave } });
          showSaved();
        } else {
          console.log('[cloud] No cloud save found — starting fresh');
          setCloudStatus(null);
        }
      } catch (err) {
        console.warn('[cloud] Load failed:', err);
        setCloudStatus('error');
      }
    })();
  }, [user, dispatch, showSaved]); // eslint-disable-line react-hooks/exhaustive-deps

  // Periodic push every 2 minutes real time (fires even when game is paused)
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(async () => {
      try {
        const token = tokenRef.current || (await user.getAccessToken());
        if (token) {
          tokenRef.current = token;
          await doSave(token, stateRef.current);
          apiPushLeaderboard(token, stateRef.current, user).catch(() => {});
        }
      } catch (err) {
        console.warn('[cloud] Periodic sync failed:', err);
        setCloudStatus('error');
      }
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, doSave]); // eslint-disable-line react-hooks/exhaustive-deps

  // Final push on page unload (keepalive so it survives tab close)
  useEffect(() => {
    if (!user) return;
    const handleUnload = () => {
      if (skipUnloadRef.current) return; // New Game / sign-out already handled save
      const token = tokenRef.current;
      if (!token) return;
      // keepalive: true is needed for beforeunload but has a 64KB limit;
      // if state is too large this silently fails — acceptable for unload path
      apiSave(token, stateRef.current, true).catch(() => {});
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [user]);

  const skipUnloadSave = useCallback(() => { skipUnloadRef.current = true; }, []);

  return { cloudStatus, doSave, skipUnloadSave };
}
